import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type CityCreate,
  type CityListParams,
  type CityUpdate,
  createCity,
  createPricingPlan,
  deletePricingPlan,
  getCities,
  getCityById,
  getDraftPricingPlanById,
  getPricingPlanById,
  getPricingPlanFields,
  getPricingPlans,
  getWhiteLabels,
  type PricingPlanDto,
  updateCity,
  updatePricingPlan,
} from "@/apis/city/city-apis";
import { queryKeys } from "./keys";

export function useCities(params: CityListParams) {
  return useQuery({
    queryKey: queryKeys.cities.list(params),
    queryFn: () => getCities(params),
    placeholderData: keepPreviousData,
  });
}

export function useCity(id: number | null) {
  return useQuery({
    queryKey: queryKeys.cities.detail(id ?? 0),
    // Query is disabled when id is null, so queryFn won't run with fallback value
    queryFn: () => getCityById(id ?? 0),
    enabled: id !== null,
  });
}

export function usePricingPlans(cityId: number | null) {
  return useQuery({
    queryKey: queryKeys.cities.pricingPlans(cityId ?? 0),
    // Query is disabled when cityId is null, so queryFn won't run with fallback value
    queryFn: () => getPricingPlans(cityId ?? 0),
    enabled: cityId !== null,
  });
}

export function usePricingPlanFields() {
  return useQuery({
    queryKey: queryKeys.cities.pricingPlanFields(),
    queryFn: getPricingPlanFields,
    staleTime: Infinity,
  });
}

export function useCreateCity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CityCreate) => createCity(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cities.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useUpdateCity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: CityUpdate }) =>
      updateCity(id, dto),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cities.detail(id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.cities.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.cities.pricingPlans(id),
      });
    },
  });
}

export function useDeletePricingPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isDraft }: { id: number; isDraft: boolean }) =>
      deletePricingPlan(id, isDraft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pricingManagement"] });
      queryClient.invalidateQueries({
        queryKey: queryKeys.cities.all,
        predicate: (query) => query.queryKey[1] === "pricingPlans",
      });
    },
  });
}

export function usePricingPlanDetail(
  planId: number | null,
  isDraft: boolean = false
) {
  return useQuery({
    queryKey: queryKeys.cities.pricingPlanDetail(planId ?? 0, isDraft),
    // Query is disabled when planId is null, so queryFn won't run with fallback value
    queryFn: () =>
      isDraft
        ? getDraftPricingPlanById(planId ?? 0)
        : getPricingPlanById(planId ?? 0),
    enabled: planId !== null,
  });
}

export function useCreatePricingPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PricingPlanDto) => createPricingPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pricingManagement"] });
      queryClient.invalidateQueries({
        queryKey: queryKeys.cities.all,
        predicate: (query) =>
          query.queryKey[1] === "pricingPlans" ||
          query.queryKey[1] === "galleryPricingPlans",
      });
    },
  });
}

export function useUpdatePricingPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      planId,
      data,
    }: {
      planId: number;
      data: Partial<PricingPlanDto>;
    }) => updatePricingPlan(planId, data),
    onSuccess: (_data, { planId }) => {
      queryClient.invalidateQueries({ queryKey: ["pricingManagement"] });
      queryClient.invalidateQueries({
        queryKey: queryKeys.cities.all,
        predicate: (query) =>
          (query.queryKey[1] === "pricingPlanDetail" &&
            query.queryKey[2] === planId) ||
          query.queryKey[1] === "pricingPlans" ||
          query.queryKey[1] === "galleryPricingPlans",
      });
    },
  });
}

export function useWhiteLabels() {
  return useQuery({
    queryKey: queryKeys.cities.whitelabels(),
    queryFn: getWhiteLabels,
    staleTime: Infinity,
  });
}
